import * as React from 'react'
import Button from '@mui/material/Button'
import Menu from '@mui/material/Menu'
import MenuItem from '@mui/material/MenuItem'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import Dialog from '@mui/material/Dialog'
import DialogTitle from '@mui/material/DialogTitle'
import DialogContent from '@mui/material/DialogContent'
import DialogActions from '@mui/material/DialogActions'
import TextField from '@mui/material/TextField'
import useLocale from '@/utils/locale/locale.hook'
import useAuth from '@/context/useAuth'

export default function SaveMenu({ title, saveModifications, items }) {
    const [anchorEl, setAnchorEl] = React.useState(null)
    const [scheduleDialogOpen, setScheduleDialogOpen] = React.useState(false)
    const [scheduleDate, setScheduleDate] = React.useState('')
    const [scheduleTime, setScheduleTime] = React.useState('')
    const open = Boolean(anchorEl)
    const locale = useLocale()
    const { me } = useAuth()
    
    // Check if user has @a-grid.com email
    const isAgridUser = me?.email?.includes('@a-grid.com')
    
    // Generate time slots with 10-minute intervals (00:00, 00:10, ... 23:50)
    const timeOptions = React.useMemo(() => {
        const slots = []
        for (let h = 0; h < 24; h++) {
            for (let m = 0; m < 60; m += 10) {
                const hourStr = h.toString().padStart(2, '0')
                const minStr = m.toString().padStart(2, '0')
                slots.push(`${hourStr}:${minStr}`)
            }
        }
        return slots
    }, [])

    const handleClick = (event) => {
        if (items.length > 0 || isAgridUser) {
            setAnchorEl(event.currentTarget)
        } else saveModifications(null)
    }
    
    const handleClose = (hash) => {
        setAnchorEl(null)
        if (hash !== undefined) {
            saveModifications(hash)
        }
    }
    
    const handleScheduleClick = () => {
        setAnchorEl(null)
        setScheduleDialogOpen(true)
        
        // Set default date to tomorrow
        const tomorrow = new Date()
        tomorrow.setDate(tomorrow.getDate() + 1)
        setScheduleDate(tomorrow.toISOString().split('T')[0])
        
        // Set time rounded to nearest 10 minutes
        const now = new Date()
        const minutes = Math.round(now.getMinutes() / 10) * 10
        let hours = now.getHours()
        let roundedMinutes = minutes

        if (minutes === 60) {
            roundedMinutes = 0
            hours += 1
        }
        
        // Handle wrapping midnight (24 -> 00)
        hours = hours % 24
        
        setScheduleTime(`${String(hours).padStart(2, '0')}:${String(roundedMinutes).padStart(2, '0')}`)
    }
    
    const handleScheduleConfirm = () => {
        setScheduleDialogOpen(false)
        saveModifications(null, { date: scheduleDate, time: scheduleTime })
    }
    
    const handleScheduleCancel = () => {
        setScheduleDialogOpen(false)
    }

    return (
        <div>
            <Button
                id="demo-positioned-button"
                aria-controls={open ? 'demo-positioned-menu' : undefined}
                aria-haspopup="true"
                aria-expanded={open ? 'true' : undefined}
                onClick={handleClick}
                disableElevation
                endIcon={items?.length > 0 || isAgridUser ? <KeyboardArrowDownIcon /> : null}
                variant="outlined"
            >
                {title}
            </Button>
            <Menu
                id="demo-positioned-menu"
                aria-labelledby="demo-positioned-button"
                anchorEl={anchorEl}
                open={open}
                onClose={() => setAnchorEl(null)}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
            >
                <MenuItem onClick={() => handleClose(null)}>
                    <div style={{ fontSize: 14 }}>{locale?.['realTime']?.['saveZone']}</div>
                </MenuItem>
                {items?.map((el) => {
                    return (
                        <MenuItem onClick={() => handleClose(el?.hash)} key={el.name}>
                            <div style={{ fontSize: 14 }}>
                                {locale?.['realTime']?.['saveGroup']}
                                <b style={{ marginLeft: 5 }}>{el.name}</b>
                            </div>
                        </MenuItem>
                    )
                })}
                {isAgridUser && (
                    <MenuItem onClick={handleScheduleClick}>
                        <div style={{ fontSize: 14 }}>{locale?.['realTime']?.['scheduleFuture'] || 'Planifier pour plus tard'}</div>
                    </MenuItem>
                )}
            </Menu>
            
            <Dialog open={scheduleDialogOpen} onClose={handleScheduleCancel}>
                <DialogTitle>{locale?.['realTime']?.['scheduleFutureTitle'] || 'Planifier une commande'}</DialogTitle>
                <DialogContent>
                    <TextField
                        label={locale?.['realTime']?.['scheduleDate'] || 'Date'}
                        type="date"
                        value={scheduleDate}
                        onChange={(e) => setScheduleDate(e.target.value)}
                        fullWidth
                        margin="normal"
                        InputLabelProps={{ shrink: true }}
                    />
                    
                    {/* Changed from type="time" to select */}
                    <TextField
                        select
                        label={locale?.['realTime']?.['scheduleTime'] || 'Heure'}
                        value={scheduleTime}
                        onChange={(e) => setScheduleTime(e.target.value)}
                        fullWidth
                        margin="normal"
                    >
                        {timeOptions.map((time) => (
                            <MenuItem key={time} value={time}>
                                {time}
                            </MenuItem>
                        ))}
                    </TextField>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleScheduleCancel}>
                        {locale?.['realTime']?.['cancel'] || 'Annuler'}
                    </Button>
                    <Button 
                        onClick={handleScheduleConfirm} 
                        variant="contained"
                        disabled={!scheduleDate || !scheduleTime}
                    >
                        {locale?.['realTime']?.['confirm'] || 'Confirmer'}
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    )
}